// Drains the offline outbox once connectivity is restored.
// Uses a raw axios instance (rather than the intercepted `api`) to avoid
// re-queueing during replay.
import axios from "axios";
import { toast } from "sonner";
import { API, tokenStore } from "./api";
import { listQueue, removeEntry, updateEntry, labelForEndpoint } from "./offlineQueue";
import { logger } from "./logger";

let draining = false;

const rawApi = axios.create({ baseURL: API, timeout: 30000 });
rawApi.interceptors.request.use((cfg) => {
  const t = tokenStore.get();
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

export async function drainQueue({ silent = false } = {}) {
  if (draining) return { drained: 0, remaining: 0, skipped: true };
  if (!navigator.onLine) return { drained: 0, remaining: 0, offline: true };

  draining = true;
  let drained = 0;
  let failed = 0;
  try {
    const entries = await listQueue();
    for (const entry of entries) {
      try {
        await rawApi.post(entry.endpoint, entry.payload);
        await removeEntry(entry.id);
        drained += 1;
      } catch (err) {
        const status = err?.response?.status;
        // Permanent client errors (4xx except 408/429) — drop so queue doesn't grow forever.
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          logger.warn(`Dropping queued ${entry.endpoint} due to ${status}`, err?.response?.data);
          await removeEntry(entry.id);
          failed += 1;
        } else {
          await updateEntry(entry.id, {
            attempts: (entry.attempts || 0) + 1,
            last_error: err?.message || "sync failed",
          });
          // Network still flaky — stop draining, retry later.
          if (!err?.response) break;
        }
      }
    }
  } finally {
    draining = false;
  }
  const remaining = (await listQueue()).length;
  if (!silent && drained > 0) {
    toast.success(`Synced ${drained} offline ${drained === 1 ? "entry" : "entries"}`);
  }
  if (!silent && failed > 0) {
    toast.error(`${failed} offline ${failed === 1 ? "entry" : "entries"} rejected — check My Orders / Daily Report.`);
  }
  return { drained, failed, remaining };
}

let listenersInstalled = false;

export function installOfflineSync() {
  if (listenersInstalled) return;
  listenersInstalled = true;
  const onOnline = () => {
    logger.info("Connectivity restored — draining outbox");
    drainQueue().catch((e) => logger.warn("drainQueue failed:", e?.message));
  };
  window.addEventListener("online", onOnline);
  // On first mount, if we're online and there's a backlog from a previous session, drain it.
  if (navigator.onLine) {
    drainQueue({ silent: true }).catch(() => {});
  }
}

export { labelForEndpoint };
